import { type ILabelsSettingValues } from '~/pages/Settings/Labels/types';

type JSONLabelSetting = {
  default_label_format: string;
  default_outbound_account: null | string;
  default_outbound_fallback_account?: null | string;
  default_outbound_fallback_service_level?: null | string;
  default_outbound_service_level: string;
  default_packing_slip_format: number;
  default_provider: string;
  default_return_account: null | string;
  default_return_service_level: string;
  default_shipment_reference_1: string;
  default_shipment_reference_2: string;
  is_auto_create_return_label: boolean;
  is_merged_labels_packing_slips: boolean;
  is_packing_slip_barcode_enabled?: boolean;
  is_packing_slip_hide_prices: boolean;
  is_signature_confirmation_selected: boolean;
  is_use_order_number: boolean;
  label_logo_type: string;
  packing_slip_text: string;
  store_policy_text: string;
};

export type LabelSettingFromJSON = {
  defaultLabelFormat: string;
  defaultOutboundAccount: null | string;
  defaultPackingSlipFormat: number;
  defaultReturnAccount: null | string;
  defaultReturnServiceLevel: string;
  isAutoCreateReturnLabels: boolean;
  isMergeLabelsAndPackingSlips: boolean;
  isRequireSignatureConfirmation: boolean;
  isUseOrderNumber: boolean;
  labelLogoType: string;
  outboundFallbackAccount?: null | string;
  outboundFallbackServiceLevel?: null | string;
  outboundReturnLabelServiceLevel: string;
  outboundServiceLevel: string;
  packingSlipBarcodeEnabled: boolean | undefined;
  packingSlipShowPrices: boolean;
  packingSlipText: string;
  refText1: string;
  refText2: string;
  storePolicyText: string;
};

const toJSON = (labelSettings: ILabelsSettingValues) => ({
  default_label_format: labelSettings.defaultLabelFormat || null,
  default_outbound_account: labelSettings.outboundServiceLevel
    ? labelSettings.outboundCarrierServiceLevelAccount?.toString()
    : null,
  default_outbound_fallback_account: labelSettings.outboundFallbackAccount || null,
  default_outbound_fallback_service_level: labelSettings.outboundFallbackServiceLevel || null,
  default_outbound_service_level: labelSettings.outboundServiceLevel?.toString() || null,
  default_packing_slip_format: labelSettings.defaultPackingSlipFormat || null,
  default_provider: labelSettings.outboundCarrierServiceLevel?.toString() || null,
  default_return_account: labelSettings.returnLabelServiceLevel
    ? labelSettings.returnLabelCarrierAccount?.toString()
    : null,
  default_return_provider: labelSettings.returnLabelCarrier?.toString() || null,
  default_return_service_level: labelSettings.returnLabelServiceLevel?.toString() || null,
  default_shipment_reference_1: labelSettings.refText1 || '',
  default_shipment_reference_2: labelSettings.refText2 || '',
  is_auto_create_return_label: labelSettings.isAutoCreateReturnLabels,
  is_merged_labels_packing_slips: labelSettings.isMergeLabelsAndPackingSlips, // TODO: is this needed
  is_packing_slip_barcode_enabled: labelSettings.packingSlipBarcodeEnabled ?? false,
  is_packing_slip_hide_prices: !labelSettings.packingSlipShowPrices || false,
  is_signature_confirmation_selected: labelSettings.isRequireSignatureConfirmation || false,
  is_use_order_number: labelSettings.isUseOrderNumber,
  label_logo_type: labelSettings.labelLogoType || null, // TODO: is this needed
  packing_slip_text: labelSettings.packingSlipText || '', // TODO: is this needed
  store_policy_text: labelSettings.storePolicyText || '',
});

const fromJSON = (data: JSONLabelSetting): LabelSettingFromJSON => ({
  defaultLabelFormat: data.default_label_format,
  defaultOutboundAccount: data.default_outbound_account,
  defaultPackingSlipFormat: data.default_packing_slip_format,
  defaultReturnAccount: data.default_return_account,
  defaultReturnServiceLevel: data.default_return_service_level,
  isAutoCreateReturnLabels: data.is_auto_create_return_label,
  isMergeLabelsAndPackingSlips: data.is_merged_labels_packing_slips,
  isRequireSignatureConfirmation: data.is_signature_confirmation_selected,
  isUseOrderNumber: data.is_use_order_number,
  labelLogoType: data.label_logo_type,
  outboundFallbackAccount: data.default_outbound_fallback_account,
  outboundFallbackServiceLevel: data.default_outbound_fallback_service_level,
  outboundReturnLabelServiceLevel: data.default_return_service_level,
  outboundServiceLevel: data.default_outbound_service_level,
  packingSlipBarcodeEnabled: data.is_packing_slip_barcode_enabled,
  packingSlipShowPrices: !data.is_packing_slip_hide_prices,
  packingSlipText: data.packing_slip_text,
  refText1: data.default_shipment_reference_1,
  refText2: data.default_shipment_reference_2,
  storePolicyText: data.store_policy_text,
});

const fromSaveResponseJSON = (
  data: JSONLabelSetting,
  submitted: ReturnType<typeof toJSON>,
): LabelSettingFromJSON =>
  fromJSON({
    ...data,
    is_packing_slip_barcode_enabled:
      data.is_packing_slip_barcode_enabled ?? submitted.is_packing_slip_barcode_enabled,
  });

export { JSONLabelSetting };
export { fromJSON as labelSettingsFromJSON };
export { fromSaveResponseJSON as labelSettingsFromSaveResponseJSON };
export { toJSON as labelSettingsToJSON };
